import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, exhaustMap, map, mergeMap, of, tap } from 'rxjs';
import { clearAllState } from '../../../core/store/actions/app.actions';
import { getUserSuccess } from '../../../user/store/actions/user.actions';
import { TourType } from '../../data/tour-config';
import { getStorageKeyByTourType } from '../../data/tour-functions';
import { TourProgress, TourProgressStatus } from '../../models/tour-progress';
import { TourProgressUpsertRequest } from '../../models/tour-progress-request';
import { TourProgressService } from '../../services/tour-progress/tour-progress.service';
import {
  loadTourProgress,
  loadTourProgressFailure,
  loadTourProgressSuccess,
  upsertTourProgress,
  upsertTourProgressFailure,
  upsertTourProgressSuccess,
} from '../actions/tour-progress.actions';

/** Version recorded for a tour migrated from the pre-server localStorage flags. */
const LEGACY_SEEN_VERSION = 1;

@Injectable()
export class TourProgressEffects {
  private actions$ = inject(Actions);
  private tourProgressService = inject(TourProgressService);

  loadOnUserSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getUserSuccess),
      map(() => loadTourProgress())
    );
  });

  /**
   * Loads the server record and folds in any legacy localStorage flags in the same pass.
   *
   * The migration must be merged into the payload of loadTourProgressSuccess rather than dispatched
   * after it. NgRx runs reducers before notifying effects, so a bare success action with an empty list
   * unblocks the waiting startTourIfUnseen gate at seenVersion 0 — and the gate would start the full
   * tour before a follow-up migration action could land. That is the exact re-show the migration exists
   * to prevent, and it is the common path: HomeComponent dispatches its gate before the GET returns.
   */
  loadTourProgress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadTourProgress),
      exhaustMap(() =>
        this.tourProgressService.getTourProgress().pipe(
          mergeMap(serverProgress => {
            const migrated = this.consumeLegacyProgress(serverProgress);
            return of(
              loadTourProgressSuccess({ progress: [...serverProgress, ...migrated] }),
              ...migrated.map(progress =>
                upsertTourProgress({
                  tourType: progress.tourTypeId,
                  seenVersion: progress.seenVersion,
                  status: progress.tourProgressStatusTypeId,
                })
              )
            );
          }),
          catchError(err => of(loadTourProgressFailure({ error: err.error?.errors })))
        )
      )
    );
  });

  // concatMap, not mergeMap: two terminal events for the same tour (a second tab, or a complete
  // racing a dismiss) would otherwise both reach the API before either had written, and the losing
  // insert would collide with the unique (user, tour) constraint.
  upsertTourProgress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(upsertTourProgress),
      concatMap(action => {
        const request: TourProgressUpsertRequest = {
          tourTypeId: action.tourType,
          seenVersion: action.seenVersion,
          tourProgressStatusTypeId: action.status,
        };

        return this.tourProgressService.upsert(request).pipe(
          map(progress => upsertTourProgressSuccess({ progress })),
          catchError(err =>
            of(
              upsertTourProgressFailure({
                error: err.error?.errors,
                tourType: action.tourType,
              })
            )
          )
        );
      })
    );
  });

  /**
   * Legacy tour flags are per-origin, not per-user, and survive sign-out. On a shared device the next
   * person to sign in would otherwise inherit them and have "completed" rows written under their
   * account for tours they never saw. Clearing on full state teardown closes that window; the
   * migration itself also removes each key as it consumes it.
   */
  purgeLegacyKeysOnSignOut$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(clearAllState),
        tap(() => this.allTourTypes().forEach(tourType => localStorage.removeItem(getStorageKeyByTourType(tourType))))
      );
    },
    { dispatch: false }
  );

  /**
   * Reads the legacy localStorage flags for tours the server has no row for, and removes each key as
   * it is consumed.
   *
   * Per-tour rather than all-or-nothing: gating on "the server returned nothing" strands the migration
   * permanently if one of several upserts fails, or if the user completes any tour before migrating.
   */
  private consumeLegacyProgress(serverProgress: TourProgress[]): TourProgress[] {
    const alreadyOnServer = new Set(serverProgress.map(p => p.tourTypeId));
    const migrated: TourProgress[] = [];

    for (const tourType of this.allTourTypes()) {
      const storageKey = getStorageKeyByTourType(tourType);
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        continue;
      }

      // Consume the key regardless of what it held: a malformed or stale entry has no further use, and
      // leaving it behind is what leaks one user's device state onto the next user of a shared device.
      localStorage.removeItem(storageKey);

      if (alreadyOnServer.has(tourType)) {
        continue;
      }

      try {
        if (JSON.parse(raw).data === 'true') {
          migrated.push({
            tourTypeId: tourType,
            seenVersion: LEGACY_SEEN_VERSION,
            tourProgressStatusTypeId: TourProgressStatus.Completed,
          });
        }
      } catch {
        // ignore malformed legacy entry
      }
    }

    return migrated;
  }

  private allTourTypes(): TourType[] {
    return Object.values(TourType).filter((value): value is TourType => typeof value === 'number');
  }
}
